/**
 * Geode Voice (UPT-2000 V1a, spec web V1 §5.1) — mesure de l'usage d'une session réelle.
 *
 * Deux parties, sans lien avec le magasin ni avec le réseau (injectés) pour rester testables :
 *   - `readResponseUsage(event)` : lecture de `response.usage` d'un événement `response.done` ;
 *   - `VoiceUsageReporter` : cumul des incréments, rapports numérotés (`seq`) toutes les
 *     `usageReportSeconds`, un seul rapport en vol, rejeu du MÊME `seq` et du MÊME corps après un
 *     échec réseau ou 5xx, rapport final (`final: true`) à l'arrêt.
 *
 * Forme lue (documentation Azure OpenAI Realtime, qui renvoie à la référence OpenAI Realtime ;
 * relue le 17.10.2026, voir le rapport) :
 *   response.done → response.usage {
 *     input_token_details  { cached_tokens, text_tokens, audio_tokens },
 *     output_token_details { text_tokens, audio_tokens } }
 * Aucun autre nom de champ n'est deviné (`image_tokens`, `total_tokens`… ne sont pas lus). Les jetons en
 * cache sont un sous-ensemble des jetons d'entrée (référence OpenAI) : le serveur ne les facture pas deux fois.
 */
import type { VoiceUsageAck, VoiceUsageBody, VoiceUsageTokens } from './voiceTypes';

/** Un compteur absent, négatif, non fini ou non entier vaut 0 (spec §5.1 n° 1). */
const counter = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value >= 0 ? value : 0;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

export const emptyUsageTokens = (): VoiceUsageTokens => ({
  inputText: 0,
  inputAudio: 0,
  outputText: 0,
  outputAudio: 0,
  cachedInput: 0,
});

/** Lit `response.usage` d'un événement `response.done` (cinq compteurs, 0 par défaut). */
export const readResponseUsage = (event: unknown): VoiceUsageTokens => {
  const usage = asRecord(asRecord(asRecord(event).response).usage);
  const input = asRecord(usage.input_token_details);
  const output = asRecord(usage.output_token_details);
  return {
    inputText: counter(input.text_tokens),
    inputAudio: counter(input.audio_tokens),
    cachedInput: counter(input.cached_tokens),
    outputText: counter(output.text_tokens),
    outputAudio: counter(output.audio_tokens),
  };
};

const addTokens = (left: VoiceUsageTokens, right: VoiceUsageTokens): VoiceUsageTokens => ({
  inputText: left.inputText + right.inputText,
  inputAudio: left.inputAudio + right.inputAudio,
  outputText: left.outputText + right.outputText,
  outputAudio: left.outputAudio + right.outputAudio,
  cachedInput: left.cachedInput + right.cachedInput,
});

/** Échec à retenter : pas de réponse (réseau), 5xx, ou erreurs passagères 401 / 408 / 429. Autre 4xx : abandon. */
export const isRetriableReportFailure = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (typeof status !== 'number') {
    return true;
  }
  return status >= 500 || status === 401 || status === 408 || status === 429;
};

export type VoiceUsageReporterOptions = {
  sessionId: string;
  /** Intervalle des rapports, en secondes (`limits.usageReportSeconds`). */
  intervalSeconds: number;
  /** Envoi d'un rapport (réseau). Lève en cas d'échec. */
  send: (sessionId: string, body: VoiceUsageBody) => Promise<VoiceUsageAck>;
  /** Horloge monotone en millisecondes (défaut `performance.now()`). */
  now?: () => number;
  /** Réponse acquittée par le serveur (hors rapport final). */
  onAck?: (ack: VoiceUsageAck) => void;
  /** Le serveur a répondu `continue: false` : la session doit s'arrêter pour cette raison. */
  onStopRequested?: (reason: 'budget' | 'max_duration') => void;
  /** Relecture rest du 18.10 (R2) : 400 `voice.usage_invalid` — le rapport est refusé (jamais rejoué) ; `detail` = ce qui l'est (`tokens`…). */
  onRefused?: (detail: string | null) => void;
  /** Délai maximal d'attente du rapport final à l'arrêt, en millisecondes (défaut 8 000). */
  finalTimeoutMs?: number;
};

type PendingReport = { body: VoiceUsageBody };

export class VoiceUsageReporter {
  private readonly now: () => number;
  private readonly finalTimeoutMs: number;
  private timer: ReturnType<typeof setInterval> | null = null;
  private startedAt: number | null = null;
  private seq = 0;
  /** Incréments reçus depuis le dernier rapport CONSTITUÉ (ils attendent le `seq` suivant). */
  private pending: VoiceUsageTokens = emptyUsageTokens();
  /** Rapport en vol (`sending`) ou à rejouer après un échec (`retry`). */
  private current: PendingReport | null = null;
  private sending: Promise<void> | null = null;
  private finished = false;
  private finishPromise: Promise<void> | null = null;

  constructor(private readonly options: VoiceUsageReporterOptions) {
    this.now = options.now ?? (() => performance.now());
    this.finalTimeoutMs = options.finalTimeoutMs ?? 8_000;
  }

  /** Ouverture de la connexion : l'horloge démarre et le minuteur des rapports s'arme (session seulement). */
  start(): void {
    if (this.startedAt !== null) {
      return;
    }
    this.startedAt = this.now();
    this.timer = setInterval(() => {
      void this.tick();
    }, Math.max(1, this.options.intervalSeconds) * 1000);
  }

  /** Secondes entières écoulées depuis l'ouverture de la connexion. */
  elapsedSeconds(): number {
    if (this.startedAt === null) {
      return 0;
    }
    return Math.max(0, Math.floor((this.now() - this.startedAt) / 1000));
  }

  /** Cumule l'usage d'un `response.done`. */
  addResponse(event: unknown): void {
    if (this.finished) {
      return;
    }
    this.pending = addTokens(this.pending, readResponseUsage(event));
  }

  /** Vrai quand le rapport final est parti (ou en cours). */
  isFinished(): boolean {
    return this.finished;
  }

  private buildBody(final: boolean): VoiceUsageBody {
    this.seq += 1;
    const body: VoiceUsageBody = {
      seq: this.seq,
      seconds: this.elapsedSeconds(),
      final,
      usage: this.pending,
    };
    this.pending = emptyUsageTokens();
    return body;
  }

  /**
   * Un tick du minuteur : rejoue le rapport échoué (même `seq`, même corps) ou en constitue un nouveau.
   * Un seul rapport en vol : un tick pendant un envoi ne fait rien.
   */
  async tick(): Promise<void> {
    if (this.finished || this.sending) {
      return;
    }
    if (!this.current) {
      this.current = { body: this.buildBody(false) };
    }
    await this.transmit(this.current);
  }

  private transmit(report: PendingReport): Promise<void> {
    const run = (async () => {
      try {
        const ack = await this.options.send(this.options.sessionId, report.body);
        if (this.current === report) {
          this.current = null;
        }
        if (!report.body.final) {
          this.options.onAck?.(ack);
          if (!ack.continue && !this.finished) {
            this.options.onStopRequested?.(ack.reason ?? 'budget');
          }
        }
      } catch (error) {
        // Un échec n'interrompt jamais la conversation. Réseau ou 5xx : le même rapport repart au tick suivant ;
        // un refus définitif (autre 4xx) abandonne ce rapport.
        if (!isRetriableReportFailure(error) && this.current === report) {
          this.current = null;
        }
        // 400 voice.usage_invalid : le serveur a refusé le rapport avant toute écriture (jetons au-delà du plafond) ; la session le sait.
        const refused = error as { name?: unknown; code?: unknown; detail?: unknown } | null;
        if (refused?.name === 'VoiceApiError' && refused.code === 'voice.usage_invalid') {
          this.options.onRefused?.(typeof refused.detail === 'string' ? refused.detail : null);
        }
      }
    })();
    const tracked: Promise<void> = run.finally(() => {
      if (this.sending === tracked) {
        this.sending = null;
      }
    });
    this.sending = tracked;
    return tracked;
  }

  /**
   * Arrêt : minuteur coupé, rapport en vol attendu, rapport échoué rejoué une fois, puis rapport final
   * (`final: true`). Idempotent ; l'attente du réseau est bornée.
   */
  finish(): Promise<void> {
    if (this.finishPromise) {
      return this.finishPromise;
    }
    this.clearTimer();
    this.finished = true;
    this.finishPromise = (async () => {
      if (this.startedAt === null) {
        return;
      }
      await this.withTimeout(async () => {
        if (this.sending) {
          await this.sending;
        }
        if (this.current) {
          await this.transmit(this.current);
        }
        await this.transmit({ body: this.buildBody(true) });
      });
    })();
    return this.finishPromise;
  }

  /**
   * Fermeture de l'onglet (`pagehide`) : le rapport final part par `sendKeepalive` (synchrone, `fetch` avec
   * `keepalive`). Rien n'est attendu ; l'échec est ignoré. Sans effet si le rapport final est déjà parti.
   */
  finishWithKeepalive(sendKeepalive: (sessionId: string, body: VoiceUsageBody) => void): void {
    this.clearTimer();
    if (this.finished || this.startedAt === null) {
      return;
    }
    this.finished = true;
    this.finishPromise = Promise.resolve();
    try {
      sendKeepalive(this.options.sessionId, this.buildBody(true));
    } catch {
      // échec ignoré
    }
  }

  /** Coupe le minuteur sans rapport (arrêt anormal avant l'ouverture de la connexion). */
  dispose(): void {
    this.clearTimer();
  }

  private clearTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private async withTimeout(work: () => Promise<void>): Promise<void> {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    try {
      await Promise.race([
        work(),
        new Promise<void>((resolve) => {
          timeout = setTimeout(resolve, this.finalTimeoutMs);
        }),
      ]);
    } finally {
      if (timeout) {
        clearTimeout(timeout);
      }
    }
  }
}
